import { LibraryCache } from 'base/library_cache';
import { generateScheme } from 'base/styleguide/concept';
import { UiStyle } from 'base/styleguide/model';
import { getUiStyleComponentSlug } from 'base/styleguide/ui_styles';
import { Button, ButtonPalette, RadioButtonGroupWrapper } from 'base/ui/button';
import { IconChecked, IconShuffleAi, IconVisibleOutline } from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import { PanelLayout } from 'base/ui/panel';
import { TextTiny } from 'base/ui/typography';
import { useRootClassName } from 'blocks/component_renderer/component_renderer';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { ConceptAPI, getGlobalConceptsStore } from './concept_store';
import { SidebarNavigation } from './routes';
import { useConceptStyleGuideVariables } from './style_application/concept_styleguide';
import {
  getUiStyleLabel,
  getUiStyleRadiusOptionIcon,
  globalUiStyleOverrideStore,
  UiStyleCss,
  UI_STYLES,
  UI_STYLE_RADIUS_OPTIONS,
} from './ui_styles';
import styles from './ui_styles_panel.css';

export const UiStylesPanel = observer(
  ({
    navigation,
    library,
  }: {
    navigation: SidebarNavigation;
    library: LibraryCache;
  }) => {
    const conceptsStore = getGlobalConceptsStore();
    const concept = conceptsStore.activeConcept;

    return (
      <PanelLayout
        title="Buttons & Forms"
        navigation={navigation}
        header={
          <ButtonPalette>
            <RadioButtonGroupWrapper>
              {UI_STYLE_RADIUS_OPTIONS.map((option) => (
                <Button
                  key={option}
                  icon={getUiStyleRadiusOptionIcon(option)}
                  variant={
                    concept.uiStyleRadiusOption === option
                      ? 'secondary'
                      : 'subtle'
                  }
                  onClick={() => concept.setUiStyleRadiusOption(option)}
                />
              ))}
            </RadioButtonGroupWrapper>
          </ButtonPalette>
        }
        footer={
          <UiStylesPanelFooter
            onShuffle={() =>
              concept.shuffle({ uiStyle: true, overrideLocked: true })
            }
          />
        }
      >
        <Rows spacing={12}>
          {UI_STYLES.map((uiStyle) => (
            <UiStyleCard
              key={uiStyle}
              uiStyle={uiStyle}
              library={library}
              concept={concept}
            />
          ))}
        </Rows>
      </PanelLayout>
    );
  },
);

type UiStylesPanelFooterProps = {
  onShuffle: () => void;
};

const UiStylesPanelFooter = ({ onShuffle }: UiStylesPanelFooterProps) => {
  return (
    <Button
      label="Shuffle"
      variant="secondary"
      icon={IconShuffleAi}
      iconColor="ai"
      onClick={onShuffle}
      tooltip="Shuffle UI styling"
      fullWidth={true}
    />
  );
};

const UiStyleCard = observer(
  ({
    uiStyle,
    library,
    concept,
  }: {
    uiStyle: UiStyle;
    library: LibraryCache;
    concept: ConceptAPI;
  }) => {
    const isSelected = concept.uiStyle === uiStyle;

    return (
      <div
        className={classNames(styles.card, isSelected && styles.selected)}
        onClick={() => concept.setUiStyle(uiStyle)}
      >
        <Inline spacing={8} xalign="space-between" yalign="center">
          <TextTiny>{getUiStyleLabel(uiStyle)}</TextTiny>
          <Inline spacing={8} yalign="center">
            <div className={styles.previewIcon}>
              <Button
                icon={IconVisibleOutline}
                variant="secondary"
                size="small"
                onMouseEnter={() => {
                  globalUiStyleOverrideStore.set(uiStyle);
                }}
                onMouseLeave={() => {
                  globalUiStyleOverrideStore.set(null);
                }}
                onClick={(e) => e.stopPropagation()}
                tooltip="Previewing"
              />
            </div>
            {isSelected && (
              <div className={styles.selectedIcon} aria-label="Selected">
                <IconChecked />
              </div>
            )}
          </Inline>
        </Inline>
        <UiStyleCardContent
          uiStyle={uiStyle}
          library={library}
          concept={concept}
          className={styles.panelCardContent}
        />
      </div>
    );
  },
);

export const UiStyleCardContent = observer(
  ({
    uiStyle,
    library,
    concept,
    interactive = true,
    className,
  }: {
    uiStyle: UiStyle;
    library: LibraryCache;
    concept: ConceptAPI;
    interactive?: boolean;
    className?: string;
  }) => {
    const rootClassName = useRootClassName();
    const variables = useConceptStyleGuideVariables(concept);

    const scheme = generateScheme(
      {
        palette: 'neutral',
        shade: 0,
      },
      concept,
    );

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    const comp = library.getCachedPublicComponentJson(
      getUiStyleComponentSlug(uiStyle, concept.cardStyle),
      {
        fetchIfMissing: true,
      },
    )?.value;
    if (!comp) return null;

    return (
      <div
        className={classNames(
          className,
          styles.cardContent,
          rootClassName,
          !interactive && styles.notInteractive,
        )}
        style={{
          ...variables,
          fontFamily: concept.state.fontFamilies.body.slug,
        }}
      >
        <Inline spacing={10}>
          <div
            className={classNames(
              'U_button-styled',
              scheme.alternate.has('primary-button') && 'U_is-alternate',
              styles.exampleButton,
            )}
            onClick={handleClick}
          >
            <div className={styles.exampleButtonLabel}>Button</div>
          </div>
          <div
            className={classNames(
              'U_button-styled U_is-secondary',
              styles.exampleButton,
            )}
            onClick={handleClick}
          >
            <div className={styles.exampleButtonLabel}>Button</div>
          </div>
        </Inline>
        <Rows spacing={0}>
          <div
            className={classNames(
              'U_form-styled_field-label',
              styles.fieldLabel,
            )}
          >
            Label
          </div>
          <input
            type="text"
            className={classNames(
              'U_form-styled_input',
              styles.exampleFormInput,
            )}
            placeholder="Placeholder"
            onClick={handleClick}
          />
        </Rows>
        <UiStyleCss
          uiStyle={uiStyle}
          cardStyle={concept.cardStyle}
          radiusOption={concept.uiStyleRadiusOption}
          library={library}
          rootClassName={rootClassName}
          removeTransitions={!interactive}
          concept={concept}
        />
      </div>
    );
  },
);
